import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { WaitlistProvider } from '@/context/WaitlistContext';
import Header from '@/components/common/Header';
import FooterSection from '@/components/homepage/FooterSection';
import WaitlistForm from '@/components/forms/WaitlistForm';
import { ArrowLeft, Clock, Heart } from 'lucide-react';

const ComingSoon = () => {
  const navigate = useNavigate();

  return (
    <WaitlistProvider>
      <div className="min-h-screen bg-gradient-to-b from-[#F7F5F2] to-white font-nunito">
        <Header showActions={false} />
        
        <main className="section-container section-padding">
          <div className="max-w-4xl mx-auto text-center">
            {/* Back Navigation */}
            <div className="mb-8 flex justify-start">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/')}
                className="text-[#6C4F77] hover:bg-[#6C4F77]/10"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </div>

            {/* Main Content */}
            <div className="mb-16">
              <div className="flex items-center justify-center gap-4 mb-8">
                <div className="w-16 h-16 bg-[#6C4F77] rounded-full flex items-center justify-center animate-pulse">
                  <Clock className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-[#6C4F77]">
                  Coming Soon
                </h1>
              </div>
              
              <p className="text-xl text-[#2C2C2C]/80 max-w-2xl mx-auto mb-8 leading-relaxed">
                We're putting the finishing touches on something beautiful. Our full app experience will be available soon!
              </p>

              <div className="flex items-center justify-center gap-2 text-[#F4978E] mb-12">
                <Heart className="w-5 h-5" />
                <span className="text-lg font-medium">Made with love for meaningful connections</span>
                <Heart className="w-5 h-5" />
              </div>
            </div>

            {/* What's Coming */}
            <Card className="mb-16 shadow-soft-hover text-left">
              <CardContent className="p-8 md:p-12">
                <h2 className="text-2xl font-semibold text-[#6C4F77] mb-6 text-center">
                  What's Coming in Beta
                </h2>
                
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-[#F4978E] rounded-full flex items-center justify-center mt-1">
                        <span className="text-white text-sm">✓</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#6C4F77] mb-1">Smart Contact Management</h3>
                        <p className="text-[#2C2C2C] text-sm">Keep track of the people who matter most</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-[#F4978E] rounded-full flex items-center justify-center mt-1">
                        <span className="text-white text-sm">✓</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#6C4F77] mb-1">Thoughtful Reminders</h3>
                        <p className="text-[#2C2C2C] text-sm">Never miss important moments or check-ins</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-[#F4978E] rounded-full flex items-center justify-center mt-1">
                        <span className="text-white text-sm">✓</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#6C4F77] mb-1">Memory Moments</h3>
                        <p className="text-[#2C2C2C] text-sm">Log and remember meaningful interactions</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-[#8BAA83] rounded-full flex items-center justify-center mt-1">
                        <span className="text-white text-sm">✓</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#6C4F77] mb-1">AI-Powered Suggestions</h3>
                        <p className="text-[#2C2C2C] text-sm">Get thoughtful ideas for staying connected</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-[#8BAA83] rounded-full flex items-center justify-center mt-1">
                        <span className="text-white text-sm">✓</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#6C4F77] mb-1">Relationship Insights</h3>
                        <p className="text-[#2C2C2C] text-sm">Understand patterns and strengthen bonds</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-[#8BAA83] rounded-full flex items-center justify-center mt-1">
                        <span className="text-white text-sm">✓</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#6C4F77] mb-1">Privacy-First Design</h3>
                        <p className="text-[#2C2C2C] text-sm">Your relationships are sacred and protected</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Waitlist CTA */}
            <Card className="shadow-soft-hover">
              <CardContent className="p-8 md:p-12 text-center">
                <h2 className="text-2xl font-semibold text-[#6C4F77] mb-4">
                  Be Among the First to Experience Wabisabi
                </h2>
                <p className="text-[#2C2C2C] mb-8 max-w-2xl mx-auto">
                  Join our waitlist to get early access when we launch our beta in July 2025. 
                  The first 1,000 members get 3 months free!
                </p>
                
                <WaitlistForm variant="default" />
                
                <p className="text-sm text-[#6C4F77]/70 mt-6">
                  Questions? Email us at <a href="mailto:hello@appwabisabi.com" className="text-[#F4978E] hover:underline">hello@appwabisabi.com</a>
                </p>
              </CardContent>
            </Card>
          </div>
        </main>
        
        <FooterSection />
      </div>
    </WaitlistProvider>
  );
};

export default ComingSoon;